import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

import { dialog, toast, formatBytes, convertHMS, i18N } from '@/common/util'
import midiasService from '@/services/midias.service'
import { midiaService } from '@/services/midias.service'

import type { FileTransferType, LoaderType, MidiaType } from '@/types/types'
import type { GetFilesResponse } from '@/services/midias.service'

interface InfoFileType { 
  id: string
  name: string
  fileType?: string
  isCoverImage?: boolean
}

export const useMidiaStore = defineStore('midias', () => {

  const transferList = reactive<FileTransferType[]>([])
  const list = ref<MidiaType[]>([])

  const status = reactive<Record<string, LoaderType>>({
    create: 'initial',
    update: 'initial',
    getAll: 'initial',
    getById: 'initial',
    delete: 'initial',
    export: 'initial',
    bonds: 'initial',
    favorites: 'initial',
    reaction: 'initial'
  })

  /**
   * Getters
   */
  const downloadList = computed(() => transferList.filter((item) => item.type === 0))
  const uploadList = computed(() => transferList.filter((item) => item.type === 1))
  const loading = computed(() => Object.values(status).some(el => el === 'loading'))
  const uploadProgress = computed(() => uploadList.value.some((el) => el.status === 1))

  let listV2 = reactive({
    status: 'initial' as LoaderType,
    time: 0,
    data: [] as GetFilesResponse[],
    uc: 0 // update counter
  })

  /**
   * Adquire a lista do depósito, atualizando novamente ao fim do tempo de vida
   */
  const lazyList = computed(() => {
    return (duration?: number) => {
      if (listV2.status === 'loading') return listV2.data

      duration = duration || (1000 * 60 * 5) // 
      if (listV2.time === 0 || (new Date().getTime() - listV2.time) > duration) {
        fetchAll()
        listV2.time = new Date().getTime()
      }

      return listV2.data
    }
  })

  /**
   * Actions
   */

  // Helpers
  const addQueue = (info: InfoFileType, type: number) => {
    const listIdx = transferList.findIndex((el) => info.id === el.id && info.fileType !== el.fileType && info.isCoverImage === el.isCoverImage)
    if (listIdx !== -1 && ![-1, 2 ,3].includes(transferList[listIdx].status)) return

    transferList.splice(listIdx, 1)

    transferList.push({
      id: info.id,
      type,
      progress: 0,
      name: info.name || '',
      controller: new AbortController(),
      status: 0,
      fileType: info.fileType ?? '',
      startTime: new Date().getTime(),
      isCoverImage: info.isCoverImage
    })

    return transferList.find((dl) => dl.id === info.id)
  }

  const fileDownload = async (id?: string, name?: string) => {
    const download = addQueue({ id, name }, 0)

    try {
      download.status = 1

      await midiasService.fileDownload(id, {
        progressCB: (progressEvent) => {
          download.progress = ((progressEvent.loaded / progressEvent.total) * 100) | 0
          if (download.progress === 100) download.status = 2
        },
        controller: download.controller
      })

      download.status = 2
    } catch (error) {
      download.status = -1
    }
  }

  const fileUpload = async (info: InfoFileType, file: File) => {
    const upload = addQueue(info, 1)
    if (!upload) return

    try {
      upload.status = 1
      toast.open({
        type: 'info',
        title: null,
        icon: 'upload',
        message: `upload ${info.isCoverImage ? i18N('toasts.repository.uploadCover') : ''} ${i18N('labels.started').toLowerCase()}`,
        duration: 2
      })

      const optionsUpload = {
        progressCB: (progressEvent) => {
          upload.progress = ((progressEvent.loaded / progressEvent.total) * 100) | 0
          upload.loaded = formatBytes(progressEvent.loaded)
          upload.total = formatBytes(progressEvent.total)

          if (upload.progress === 100) {
            upload.status = 2
          }
          const timeElapsed = new Date().getTime() - upload.startTime
          const uploadSpeed = (progressEvent.loaded / (timeElapsed/1000))
          upload.timeRemaining = convertHMS((progressEvent.total - progressEvent.loaded) / uploadSpeed)
        },
        controller: upload.controller
      }

      const result = info.isCoverImage 
        ? await midiasService.fileUploadCoverImage(info.id, file, optionsUpload)
        : await midiasService.fileUpload(info.id, file, optionsUpload)

      upload.status = 3
      upload.endTime = new Date().getTime()
      
      toast.success({
        title: null,
        message: `${info.name} enviado com sucesso`
      })
      
      return result
    } catch (error) {
      console.log(error)
      upload.status = -1
    } finally {
      await getAll()
    }
  }

  const cancelTransfer = (id: string) => {
    const listIdx = transferList.findIndex((el) => id === el.id)
    if (listIdx === -1) return

    if (transferList[listIdx].status === 1) {
      transferList[listIdx].controller.abort()
      transferList[listIdx].status = -2
      toast.open({
        type: 'warning',
        title: i18N('labels.transfer'),
        message: i18N('toasts.repository.cancelTransfer')
      })
    }
  }

  const removeTransfer = (id: string) => {
    const listIdx = transferList.findIndex((el) => id === el.id)
    if (listIdx !== -1 && (transferList[listIdx].status < 0 || transferList[listIdx].status === 3))
      transferList.splice(listIdx, 1)
  }

  const clearTransfer = () => {
    const temp = transferList
    temp.forEach((el, index) =>  el.status < 0 || el.status === 3 ? transferList.splice(index, 1) : false)
  }

  /**
   * Busca todas as mídias do depósito
   * Usa listaV2 para armazenar a lista de mídias
   * @param opt 
   */
  const fetchAll = async (opt?: any) => {
    listV2.status = 'loading'

    return midiaService.getFiles(opt)
      .then(({ data }) => {
        listV2.data = data
        listV2.status = 'success'
      }).catch(() => {
        listV2.status = 'error'
      })
  }
  
  const getAll = async () => {
    status.getAll = 'loading'
    try {
      const result = await midiasService.get()
      status.getAll = 'success' 
      list.value = result.data
    } catch {
      status.getAll = 'error'
    }
  }

  const remove = async (midia: MidiaType, force?: boolean) => {
    if (!force) { 
      return await dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', { type: i18N('labels.media').toLowerCase(), name: midia.nome }),
        complete: () => remove(midia, true)
      })
    }

    try {
      status.delete = 'loading'
      const data = await midiasService.remove(midia.id)

      if (data?.success) {
        getAll()
        listV2.time = 0
      }

      status.delete = 'success'

      return data
    } catch {
      status.delete = 'error'
    }
  }

  const create = async (dataObj: MidiaType, file: File, coverFile?: File): Promise<MidiaType> => {
    status.create = 'loading'

    try {
      const {success, data} = await midiasService.create(dataObj)
      const result = data as MidiaType

      if (file && success) {
        fileUpload({id: result.id, name: file.name, fileType: file.type}, file)
      }

      if (coverFile && success) {
        fileUpload({id: result.id, name: coverFile.name, fileType: coverFile.type, isCoverImage: true}, coverFile)
      }
      
      if (success && !file && !coverFile) getAll()
      if (success) listV2.time = 0
      
      status.create = 'success'
      return data
    } catch (error) {
      status.create = 'error'
    }
  }

  const update = async (id: string, dataObj: MidiaType, file: File, coverFile?: File): Promise<MidiaType> => {
    status.update = 'loading'

    try {
      const { data, success} = await midiasService.update<MidiaType>(id, dataObj)

      if (file && success) {
        fileUpload({id: data.id, name: file.name, fileType: file.type}, file)
      }

      if (coverFile && success) {
        fileUpload({id: data.id, name: coverFile.name, fileType: coverFile.type, isCoverImage: true}, coverFile)
      }

      if (success) {
        getAll()
        listV2.time = 0
      }
      
      status.update = 'success'
      return data
    } catch (error) {
      status.update = 'error'
    }
  }

  const exportMidias = async () => {
    status.export = 'loading'

    try {
      const csvFile = await midiasService.exportCsv()
      status.export = 'success'
      return csvFile
    } catch {
      status.export = 'error'
    }
  }

  const bonds = async (midiaId: string) => {
    status.bonds = 'loading'
    try {
      const {data} = await midiasService.getBonds(midiaId)
      status.bonds = 'success'
      return data
    } catch {
      status.bonds = 'error'
    }
  }

  const getById = async (midiaId: string) => {
    status.getById = 'loading'
    try {
      const { data } = await midiasService.get(midiaId)
      status.getById = 'success'
      return data
    } catch {
      status.getById = 'error'
    }
  }

  const setFavorite = async(id: string, favorite?: boolean) => {
    status.favorites = 'loading'

    try {
      const { success } = (favorite ? await midiaService.bookmark(id) : await midiaService.unbookmark(id))
      status.favorites = 'success'

      return success
    } catch {
      status.favorites = 'error'
    }
  }

  const setReactionMedia = async (objData: { id: string, reacao?: string }) => {
    status.reaction = 'loading'
    try {
      const data = await midiaService.setReactionMedia(objData)
      status.reaction = 'success'

      return data
    } catch {
      status.reaction = 'error'
    }
  }

  return {
    transferList,
    list,
    status,
    // getters
    lazyList,
    listStatus: listV2.status,
    downloadList,
    uploadList,
    loading,
    uploadProgress,
    // actions
    fileDownload,
    fileUpload,
    cancelTransfer,
    removeTransfer,
    getAll,
    remove,
    create,
    update,
    exportMidias,
    bonds,
    clearTransfer,
    getById,
    setFavorite,
    setReactionMedia
  }
})
